import React, { useEffect, useRef, useState } from 'react';
import PommeLogo from '../assets/POMME LOGO.svg';

// Import des composants nécessaires
import VideoWithSound from '../components/VideoWithSound';
import LoadingScreen from '../components/LoadingScreen';

const OnePage: React.FC = () => {
  // État pour gérer l'écran de chargement (activé)
  const [isLoading, setIsLoading] = useState(true);
  // État pour suivre la progression réelle du chargement des vidéos
  const [videoLoadingProgress, setVideoLoadingProgress] = useState(0);
  // État pour suivre si toutes les vidéos sont chargées
  const [videosLoaded, setVideosLoaded] = useState(false);
  
  // Références pour les vidéos
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  // État pour suivre si les vidéos sont en pause (cliquées par l'utilisateur)
  const [pausedVideos, setPausedVideos] = useState<{[key: number]: boolean}>({});
  // État pour suivre si le son est activé pour chaque vidéo
  const [unmutedVideos, setUnmutedVideos] = useState<{[key: number]: boolean}>({});
  // État pour détecter si l'appareil est mobile
  const [isMobile, setIsMobile] = useState(false);
  
  // Fonction pour terminer le chargement avec optimisation pour mobile
  const handleLoadingComplete = () => {
    // Forcer la fin du chargement après un délai fixe pour éviter les blocages
    console.log(`Chargement terminé avec ${videoLoadingProgress}% des vidéos chargées`);
    setIsLoading(false);
    
    // Sur mobile, libérer la mémoire des vidéos non visibles après le chargement
    if (isMobile) {
      setTimeout(() => {
        // Libérer la mémoire des vidéos qui ne sont pas visibles dans la fenêtre
        videoRefs.current.forEach((video, index) => {
          if (video && !isElementInViewport(video) && !video.paused) {
            // Mettre en pause et libérer la mémoire
            video.pause();
            // Utiliser un attribut de donnée pour marquer la vidéo comme déchargée
            video.dataset.lazyLoaded = 'false';
          }
        });
      }, 1000);
    }
  };
  
  // Fonction utilitaire pour vérifier si un élément est visible dans la fenêtre
  const isElementInViewport = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };
  
  // Fonction pour gérer le clic sur une vidéo
  const handleVideoClick = (index: number) => {
    const video = videoRefs.current[index];
    if (!video) return;
    
    try {
      if (video.paused) {
        // Si la vidéo est en pause, on la joue
        // Mettre à jour l'état avant de lancer la lecture pour éviter les conflits
        setPausedVideos(prev => ({ ...prev, [index]: false }));
        // Utiliser un délai plus long pour éviter les conflits
        setTimeout(() => {
          if (videoRefs.current[index] && videoRefs.current[index]?.paused) {
            videoRefs.current[index]?.play().catch(error => {
              // Ignorer spécifiquement les erreurs AbortError qui sont normales
              if (error.name !== 'AbortError') {
                console.error("Erreur lors de la lecture de la vidéo:", error);
              }
            });
          }
        }, 50);
      } else {
        // Si la vidéo est en lecture, on la met en pause
        // Mettre à jour l'état avant de mettre en pause pour éviter les conflits
        setPausedVideos(prev => ({ ...prev, [index]: true }));
        video.pause();
      }
    } catch (error) {
      console.error("Erreur lors de la gestion du clic vidéo:", error);
    }
  };


  
  // Configurer l'Intersection Observer pour les vidéos
  useEffect(() => {
    // Optimiser les options de l'Intersection Observer en fonction de l'appareil
    const options = {
      root: null,
      rootMargin: isMobile ? '100px' : '0px', // Marge plus grande sur mobile pour précharger plus tôt
      threshold: isMobile ? 0.1 : 0.3 // Seuil plus bas sur mobile pour déclencher plus tôt
    };
    
    const handleIntersect = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      // Optimisation: traiter les entrées par lots pour réduire la charge CPU
      const visibleEntries: IntersectionObserverEntry[] = [];
      const hiddenEntries: IntersectionObserverEntry[] = [];
      
      // Trier les entrées par visibilité
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          visibleEntries.push(entry);
        } else {
          hiddenEntries.push(entry);
        }
      });
      
      // Traiter d'abord les vidéos visibles avec un léger délai pour éviter les conflits
      if (visibleEntries.length > 0) {
        // Sur mobile, ajouter un petit délai pour éviter de surcharger le CPU
        const delay = isMobile ? 100 : 0;
        
        setTimeout(() => {
          visibleEntries.forEach(entry => {
            const video = entry.target as HTMLVideoElement;
            const index = videoRefs.current.findIndex(v => v === video);
            
            // La vidéo est visible, on la joue seulement si elle n'a pas été mise en pause par l'utilisateur
            if (!pausedVideos[index]) {
              if (video.paused) {
                try {
                  // Sur mobile, forcer le mute pour permettre l'autoplay
                  if (isMobile && !video.muted) {
                    video.muted = true;
                  }
                  
                  // Utiliser une promesse pour gérer les erreurs proprement
                  video.play().catch(error => {
                    if (error.name === 'NotAllowedError') {
                      console.log('Autoplay bloqué sur mobile, tentative avec mute');
                      if (!video.muted) {
                        video.muted = true;
                        video.play().catch(err => {
                          console.error('Échec de lecture même avec mute:', err);
                        });
                      }
                    } else {
                      console.error("Erreur lors de la lecture automatique de la vidéo:", error);
                    }
                  });
                } catch (error) {
                  console.error("Erreur lors de la lecture automatique de la vidéo:", error);
                }
              }
            }
          });
        }, delay);
      }
      
      // Traiter ensuite les vidéos non visibles
      hiddenEntries.forEach(entry => {
        const video = entry.target as HTMLVideoElement;
        const index = videoRefs.current.findIndex(v => v === video);
        
        // La vidéo n'est plus visible, on la met en pause sauf si l'utilisateur l'a explicitement mise en pause
        if (!pausedVideos[index] && !video.paused) {
          // Sur mobile, libérer les ressources plus agressivement
          if (isMobile) {
            // Réduire la qualité ou mettre en pause immédiatement
            video.pause();
            // Optionnel: libérer la mémoire vidéo sur les appareils avec peu de RAM
            if (video.readyState > 2) {
              // Seulement si la vidéo a déjà été chargée
              video.load(); // Réinitialise la vidéo et libère la mémoire
            }
          } else {
            // Sur desktop, simplement mettre en pause
            video.pause();
          }
        }
      });
    };
    
    // Créer l'observer
    const observer = new IntersectionObserver(handleIntersect, options);
    
    // Observer toutes les vidéos avec un délai pour éviter de surcharger le navigateur au chargement
    setTimeout(() => {
      videoRefs.current.forEach(video => {
        if (video) {
          observer.observe(video);
        }
      });
    }, isMobile ? 300 : 100);
    
    // Nettoyer l'observer lors du démontage
    return () => {
      videoRefs.current.forEach(video => {
        if (video) {
          observer.unobserve(video);
        }
      });
    };
  }, [pausedVideos, isMobile]);
  
  // Initialiser les vidéos au chargement, mais ne pas essayer de les lire automatiquement
  
  // Détecter si l'appareil est mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Effet pour précharger les vidéos pendant l'écran de chargement avec stratégie intelligente
  useEffect(() => {
    // Liste complète des chemins des vidéos
    const allVideoPaths = [
      '/videos/Event1.mp4',
      '/videos/Event2.mp4',
      '/videos/Event4.mp4',
      '/videos/Event5.mp4',
      '/videos/Event6.mp4',
      '/videos/Event7.mp4',
      '/videos/Event8.mp4',
      '/videos/Event9.mp4',
      '/videos/Event10.mp4', // Vidéo qui avait des problèmes de lecture/pause
      '/videos/Sky Bon.mp4',
      '/videos/Dj Lucky Twiga Screen Visual.mp4',
      '/videos/Dj Lucky Authoban.mp4',
      '/videos/Product.mp4',
      '/videos/Product1.mp4',
      '/videos/Daghe Video 2.mp4',
      '/videos/Full.mp4',
      '/videos/Full1.mp4',
      '/videos/Gp2024 Screen Visuals.mp4',
      '/videos/Pinoc & Enemies.mp4',
      '/videos/Artverse 2D Flyer.mp4',
      '/videos/Youth Intro.mp4',
      '/videos/Exposition S².mp4'
    ];
    
    // Trier les vidéos par taille (plus petites d'abord) pour optimiser le chargement
    const videoSizes: {[key: string]: number} = {
      '/videos/Event10.mp4': 23.28, // Vidéo qui avait des problèmes de lecture/pause
      '/videos/Event1.mp4': 2.67,
      '/videos/Event2.mp4': 53.68,
      '/videos/Event4.mp4': 4.5,
      '/videos/Event5.mp4': 18.96,
      '/videos/Event6.mp4': 20.8,
      '/videos/Event7.mp4': 11.52,
      '/videos/Event8.mp4': 16.41,
      '/videos/Event9.mp4': 10.79,
      '/videos/Sky Bon.mp4': 32.6,
      '/videos/Dj Lucky Twiga Screen Visual.mp4': 170.39,
      '/videos/Dj Lucky Authoban.mp4': 13.84,
      '/videos/Product.mp4': 19.46,
      '/videos/Product1.mp4': 5.88,
      '/videos/Daghe Video 2.mp4': 21.86,
      '/videos/Full.mp4': 73.31,
      '/videos/Full1.mp4': 14.71,
      '/videos/Gp2024 Screen Visuals.mp4': 28.37,
      '/videos/Pinoc & Enemies.mp4': 11.52,
      '/videos/Artverse 2D Flyer.mp4': 1.17,
      '/videos/Youth Intro.mp4': 2.82,
      '/videos/Exposition S².mp4': 6.4,
      '/videos/Twiga Screen Visual.mp4': 88.24,
      '/videos/Event..mp4': 9.39
    };
    
    // Vidéos prioritaires (celles visibles au chargement initial)
    const priorityVideos = [
      '/videos/Event10.mp4', // Vidéo qui avait des problèmes de lecture/pause
      '/videos/Event1.mp4',
      '/videos/Event2.mp4',
      '/videos/Event4.mp4'
    ];
    
    // Précharger les vidéos avec stratégie intelligente
    const preloadVideosIntelligent = async () => {
      try {
        // Étape 1: Précharger uniquement les métadonnées de toutes les vidéos pour accélérer le chargement initial
        console.log('Préchargement des métadonnées de toutes les vidéos...');
        
        let loadedCount = 0;
        const totalInitialVideos = isMobile ? priorityVideos.length : allVideoPaths.length;
        
        // Fonction pour mettre à jour la progression
        const updateProgress = (increment = 1) => {
          loadedCount += increment;
          const progress = Math.floor((loadedCount / totalInitialVideos) * 100);
          setVideoLoadingProgress(progress);
          return progress;
        };
        
        // Précharger les métadonnées de toutes les vidéos
        const metadataPromises = (isMobile ? priorityVideos : allVideoPaths).map(path => {
          return new Promise<void>((resolve) => {
            // Créer un élément vidéo temporaire
            const video = document.createElement('video');
            video.preload = 'metadata';
            video.muted = true;
            video.playsInline = true;
            video.src = path;
            
            // Écouter l'événement loadedmetadata
            video.addEventListener('loadedmetadata', () => {
              const progress = updateProgress();
              console.log(`Métadonnées de ${path} chargées (${progress}%)`);
              resolve();
            }, { once: true });
            
            // Gérer les erreurs
            video.addEventListener('error', () => {
              console.warn(`Erreur lors du chargement des métadonnées de ${path}`);
              updateProgress();
              resolve();
            }, { once: true });
            
            // Définir un timeout pour éviter de bloquer indéfiniment
            setTimeout(() => {
              if (!video.readyState) {
                console.warn(`Timeout lors du chargement des métadonnées de ${path}`);
                updateProgress();
                resolve();
              }
            }, 3000);
            
            // Démarrer le chargement des métadonnées
            video.load();
          });
        });
        
        // Attendre que toutes les métadonnées soient chargées
        await Promise.all(metadataPromises);
        
        // Marquer comme chargé pour permettre l'accès au site
        setVideosLoaded(true);
        setVideoLoadingProgress(100);
        
        // Forcer la fin de l'écran de chargement après un délai maximum
        setTimeout(() => {
          setIsLoading(false);
        }, 3000);
        
        // Étape 2: Si on est sur mobile, charger progressivement le reste des vidéos en arrière-plan
        if (isMobile) {
          console.log('Chargement progressif des vidéos restantes en arrière-plan...');
          
          // Trier les vidéos restantes par taille (plus petites d'abord)
          const remainingVideos = allVideoPaths
            .filter(path => !priorityVideos.includes(path))
            .sort((a, b) => (videoSizes[a] || 999) - (videoSizes[b] || 999));
          
          // Charger les vidéos par lots pour éviter de surcharger le navigateur
          const batchSize = 3;
          const batches = [];
          
          for (let i = 0; i < remainingVideos.length; i += batchSize) {
            batches.push(remainingVideos.slice(i, i + batchSize));
          }
          
          // Charger chaque lot séquentiellement
          for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            console.log(`Chargement du lot ${i+1}/${batches.length} : ${batch.join(', ')}`);
            
            // Attendre que le lot précédent soit terminé avant de commencer le suivant
            await new Promise<void>(resolve => {
              setTimeout(() => {
                batch.forEach(path => {
                  const video = document.createElement('video');
                  video.preload = 'metadata';
                  video.muted = true;
                  video.src = path;
                  video.load();
                });
                resolve();
              }, 2000 * i); // Délai progressif entre les lots
            });
          }
        }
        
        console.log('Stratégie de préchargement intelligent terminée avec succès');
        setVideoLoadingProgress(100);
      } catch (error) {
        console.error('Erreur lors du préchargement intelligent des vidéos:', error);
        // Même en cas d'erreur, permettre l'accès au site
        setVideosLoaded(true);
        setVideoLoadingProgress(100);
      }
    };
    
    preloadVideosIntelligent();
    
    // Nettoyer les vidéos non visibles lors du changement de section
    const cleanupUnusedVideos = () => {
      if (!isMobile) return;
      
      videoRefs.current.forEach((video, index) => {
        if (video && !isElementInViewport(video) && !video.paused) {
          // Libérer les ressources des vidéos non visibles
          video.pause();
          video.preload = 'metadata';
          video.currentTime = 0;
        }
      });
    };
    
    // Ajouter un gestionnaire d'événements pour le défilement
    window.addEventListener('scroll', cleanupUnusedVideos, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', cleanupUnusedVideos);
    };
  }, [isMobile]);

  // Effet pour gérer le défilement automatique vers l'ancre dans l'URL lors du chargement de la page
  useEffect(() => {
    // Attendre que le DOM soit complètement chargé et que l'écran de chargement soit terminé
    if (!isLoading) {
      setTimeout(() => {
        // Si l'URL contient une ancre, faire défiler vers cette section
        if (window.location.hash) {
          const sectionId = window.location.hash.substring(1); // Enlever le # du début
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }, 300);
    }
  }, [isLoading]);
  
  // Initialiser les vidéos au chargement, mais ne pas essayer de les lire automatiquement
  // pour éviter les conflits entre play() et pause()
  useEffect(() => {
    const timer = setTimeout(() => {
      videoRefs.current.forEach((video, index) => {
        if (video) {
          // Configurer les attributs des vidéos
          video.muted = !unmutedVideos[index];
          video.playsInline = true;
          video.load();
          
          // Ne pas essayer de lire les vidéos ici pour éviter les conflits
          // L'attribut autoPlay et l'Intersection Observer s'en chargeront
        }
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Nettoyer les vidéos lors du démontage du composant
  useEffect(() => {
    return () => {
      // Mettre en pause toutes les vidéos lors du démontage
      videoRefs.current.forEach(video => {
        if (video && !video.paused) {
          video.pause();
        }
      });
    };
  }, []);
  // Effet pour précharger les vidéos pendant l'écran de chargement
  useEffect(() => {
    if (!isLoading) return;
    
    // Liste complète des chemins des vidéos
    const allVideoPaths = [
      '/videos/Event1.mp4',
      '/videos/Event2.mp4',
      '/videos/Event4.mp4',
      '/videos/Event5.mp4',
      '/videos/Event6.mp4',
      '/videos/Event7.mp4',
      '/videos/Event8.mp4',
      '/videos/Event9.mp4',
      '/videos/Event10.mp4',
      '/videos/Sky Bon.mp4',
      '/videos/Dj Lucky Twiga Screen Visual.mp4',
      '/videos/Dj Lucky Authoban.mp4',
      '/videos/Product.mp4',
      '/videos/Product1.mp4',
      '/videos/Daghe Video 2.mp4',
      '/videos/Full.mp4',
      '/videos/Full1.mp4',
      '/videos/Gp2024 Screen Visuals.mp4',
      '/videos/Pinoc & Enemies.mp4',
      '/videos/Artverse 2D Flyer.mp4',
      '/videos/Youth Intro.mp4',
      '/videos/Exposition S².mp4'
    ];
    
    // Précharger les vidéos avec stratégie optimisée
    const preloadVideos = async () => {
      try {
        // Précharger uniquement les métadonnées pour accélérer le chargement initial
        console.log('Préchargement des métadonnées des vidéos...');
        
        let loadedCount = 0;
        const totalVideos = allVideoPaths.length;
        
        // Fonction pour mettre à jour la progression
        const updateProgress = (increment = 1) => {
          loadedCount += increment;
          const progress = Math.floor((loadedCount / totalVideos) * 100);
          setVideoLoadingProgress(progress);
          return progress;
        };
        
        // Précharger les métadonnées des vidéos par lots
        const batchSize = 4;
        const batches = [];
        
        for (let i = 0; i < allVideoPaths.length; i += batchSize) {
          batches.push(allVideoPaths.slice(i, i + batchSize));
        }
        
        for (let i = 0; i < batches.length; i++) {
          const batch = batches[i];
          console.log(`Chargement du lot ${i+1}/${batches.length}`);
          
          const batchPromises = batch.map(path => {
            return new Promise<void>((resolve) => {
              const video = document.createElement('video');
              video.preload = 'metadata';
              video.muted = true;
              video.playsInline = true;
              video.src = path;
              
              video.addEventListener('loadedmetadata', () => {
                updateProgress();
                resolve();
              }, { once: true });
              
              video.addEventListener('error', () => {
                console.warn(`Erreur lors du chargement de ${path}`);
                updateProgress();
                resolve();
              }, { once: true });
              
              setTimeout(() => {
                if (!video.readyState) {
                  updateProgress();
                  resolve();
                }
              }, 3000);
              
              video.load();
            });
          });
          
          await Promise.all(batchPromises);
        }
        
        setVideosLoaded(true);
        setVideoLoadingProgress(100);
        console.log('Préchargement terminé avec succès');
      } catch (error) {
        console.error('Erreur lors du préchargement des vidéos:', error);
        setVideosLoaded(true);
        setVideoLoadingProgress(100);
      }
    };
    
    preloadVideos();
  }, [isLoading]);
  
  return (
    <div className="one-page-container">
      {/* Écran de chargement avec animation de la pomme */}
      {isLoading && (
        <LoadingScreen 
          onLoadingComplete={() => setIsLoading(false)}
          minDisplayTime={4000}
          videoLoadingProgress={videoLoadingProgress}
        />
      )}
      {/* Section Home */}
      <section id="home" className="min-h-screen flex flex-col items-center justify-center relative">
        <div className="flex flex-col items-center w-full h-full">
          <img 
            src={PommeLogo} 
            alt="POMME LOGO" 
            className="w-80 h-80 md:w-96 md:h-96 lg:w-[32rem] lg:h-[32rem] xl:w-[40rem] xl:h-[40rem] -mt-20" 
          />
        </div>
      </section>

      {/* Section Social Media */}
      <section id="social-media" className="min-h-screen py-16 px-4 md:px-8">
        <div className="container mx-auto">
          <h1 className="text-3xl md:text-4xl font-light tracking-wider mb-2 text-black">SOCIAL MEDIA 3D TEASER</h1>
          <div className="w-16 h-px bg-black mb-4"></div>
          <h2 className="text-xl md:text-2xl font-extralight tracking-wide mb-8 text-gray-800 uppercase">
            Event Promotion
          </h2>
          <div className="content">
            {/* Contenu de la section Social Media */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {/* Event10.mp4 - Corrigé avec le même système que les autres vidéos */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event10.mp4"
                    index={24}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1REG5VtrpEDSe-H78CiHKR-oENVjx15br/view?usp=drive_link"
                    title="Event 10 - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Event1.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event1.mp4"
                    index={0}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/18EuKhkjL26iYZik6Ity_L7gYtxJ-4VjW/view?usp=drive_link"
                    title="Event 1 - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Event2.mov */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event2.mp4"
                    index={1}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1w6YX7lYoNOPIsBxEQEPDJDNLiquTaR_z/view?usp=drive_link"
                    title="Event 2 - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Sky Bon.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Sky Bon.mp4"
                    index={20}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1OWPy_g7eV7HNUKopoGROYbvnGEmVVReq/view?usp=drive_link"
                    title="Sky Bon - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Event..mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event..mp4"
                    index={2}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1xAm1aGtB-9WmQOdYuUNkFqlggC3c9ToV/view?usp=drive_link"
                    title="Event.. - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Event4.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event4.mp4"
                    index={3}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1NTMT-kzrh1uPJMa3wWNVKA8R9gPcutJp/view?usp=drive_link"
                    title="Event 4 - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Event5.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event5.mp4"
                    index={4}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/123CXTAGHl4cdprI5ykmykA7IeM1B5LGW/view?usp=drive_link"
                    title="Event 5 - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Event6.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event6.mp4"
                    index={5}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/15-wFWO0iqaaV8_gJXbzqCKZS3oKxBf9l/view?usp=drive_link"
                    title="Event 6 - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Event7.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event7.mp4"
                    index={6}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1RCHiF3f0kEcvl_d_f-0c158zm2hQrMkc/view?usp=drive_link"
                    title="Event 7 - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Event8.mov */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event8.mp4"
                    index={7}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/16hxI2TeN12gYGU0ID21X-gBys3yNOnHB/view?usp=drive_link"
                    title="Event 8 - Social Media 3D Teaser"
                  />
                </div>
              </div>
              
              {/* Event9.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Event9.mp4"
                    index={8}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1APgakBAbhWLVv50lIQhv8Ox91FvUbOGy/view?usp=drive_link"
                    title="Event 9 - Social Media 3D Teaser"
                  />
                </div>
              </div>

              {/* Dj Lucky Authoban */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Dj Lucky Authoban.mp4"
                    index={23}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1mwVUKXT-k_0uvdQFboYOvLlKt1kHYrJG/view?usp=drive_link"
                    title="Dj Lucky Authoban"
                  />
                </div>
              </div>
            </div>
            
            <h2 id="product-promotion" className="text-xl md:text-2xl font-extralight tracking-wide mt-16 mb-8 text-gray-800 uppercase">
              Product Promotion
            </h2>
            
            {/* Contenu de la section Product Promotion */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {/* Product.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Product.mp4"
                    index={9}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1kEZosfphCnIPeiu39ltfzIFJ4C8WPC_l/view?usp=drive_link"
                    title="Product - Product Promotion"
                  />
                </div>
              </div>
              
              {/* Product1.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Product1.mp4"
                    index={10}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1HlpFZE6yRy7lbme3AugnzKCcfie20bTC/view?usp=drive_link"
                    title="Product 1 - Product Promotion"
                  />
                </div>
              </div>
            </div>
            
            {/* Troisième vidéo dans une nouvelle ligne - DAGHE VIDEO 2.MOV */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {/* Daghe Video 2.mp4 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Daghe Video 2.mp4"
                    index={25}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/14JuJum9gp2SwcdQF4jIsfzUZ_s6nuoQU/view?usp=drive_link"
                    title="Daghe Video 2 - Product Promotion"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Scenographie Screen Animation */}
      <section id="scenography" className="min-h-screen py-16 px-4 md:px-8">
        <div className="container mx-auto">
          <h1 className="text-3xl md:text-4xl font-light tracking-wider mb-2 text-black">SCENOGRAPHIE SCREEN ANIMATION</h1>
          <div className="w-16 h-px bg-black mb-8"></div>
          <div className="content">
            
            {/* Contenu de la section Scenography */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {/* Vidéo 1 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Dj Lucky Twiga Screen Visual.mp4"
                    index={11}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1fqal3sMRZCPuolTAa82AeVr-oEBnOtj7/view?usp=drive_link"
                    title="Dj Lucky Twiga Screen Visual - Scenographie"
                  />
                </div>
              </div>
              
              {/* Vidéo 2 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Gp2024 Screen Visuals.mp4"
                    index={12}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1ZAT9xpXUlR_GuL5c3vxifPkEzQ-ieHpB/view?usp=drive_link"
                    title="Gp2024 Screen Visuals - Scenographie"
                  />
                </div>
              </div>
              
              {/* Vidéo 3 */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Twiga Screen Visual.mp4"
                    index={13}
                    videoRefs={videoRefs}
                    driveUrl="https://drive.google.com/file/d/1al3Z7f27bPLpfMSbjqofQu9VAPcvF0TP/view?usp=drive_link"
                    title="Twiga Screen Visual - Scenographie"
                    handleVideoClick={handleVideoClick}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Full Video Clip / Edit */}
      <section id="full-video" className="min-h-screen py-16 px-4 md:px-8">
        <div className="container mx-auto">
          <h1 className="text-3xl md:text-4xl font-light tracking-wider mb-2 text-black">FULL VIDEO CLIP / EDIT</h1>
          <div className="w-16 h-px bg-black mb-8"></div>
          <div className="content">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {/* Colonne de gauche */}
              <div className="flex flex-col space-y-8">
                {/* Vidéo YouTube intégrée */}
                <div className="video-item flex flex-col">
                  <div className="relative rounded shadow-md overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/2ZI3TK9hwzQ"
                      title="YouTube video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
                
                {/* Vidéo Full.mp4 - Maintenant sous la vidéo YouTube */}
                <div className="video-item flex flex-col">
                  <div className="relative rounded shadow-md overflow-hidden">
                    <VideoWithSound
                      src="/videos/Full.mp4"
                      index={14}
                      videoRefs={videoRefs}
                      handleVideoClick={handleVideoClick}
                      driveUrl="https://drive.google.com/file/d/10IS54vuW3G02ZOAu97DDTfyb1oWsUXCs/view?usp=drive_link"
                      title="Full Video Clip / Edit"
                    />
                  </div>
                </div>
                
                {/* Vidéo YouTube avec les mêmes dimensions que Full.mp4 */}
                <div className="video-item flex flex-col mt-8">
                  <div className="relative rounded shadow-md overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/0OMD0u1qYQo"
                      title="YouTube video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>
              </div>
              
              {/* Colonne de droite - Vidéo 2 */}
              <div className="flex flex-col h-full">
                <div className="video-item flex-1">
                  <div className="relative rounded shadow-md overflow-hidden h-[95%]">
                    <VideoWithSound
                      src="/videos/Full1.mp4"
                      index={15}
                      videoRefs={videoRefs}
                      handleVideoClick={handleVideoClick}
                      driveUrl="https://drive.google.com/file/d/1pFJmXOfJQxWLsIM73jYO2Pu6Re36Dh-A/view?usp=drive_link"
                      title="Full1 Video Clip / Edit"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Vidéo 2D / Flyer */}
      <section id="2d-video" className="min-h-screen py-16 px-4 md:px-8">
        <div className="container mx-auto">
          <h1 className="text-3xl md:text-4xl font-light tracking-wider mb-2 text-black">VIDÉO 2D / FLYER</h1>
          <div className="w-16 h-px bg-black mb-8"></div>
          <div className="content">
            {/* Contenu de la section Vidéo 2D / Flyer */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {/* ArtVerse 2D Flyer */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Artverse 2D Flyer.mp4"
                    index={16}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1f6WfAHomqhpq2Endcp58EPSAxZs_F0dL/view?usp=drive_link"
                    title="Artverse 2D Flyer - Vidéo 2D / Flyer"
                  />
                </div>
              </div>
              
              {/* Exposition S² */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Exposition S².mp4"
                    index={22}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/1YRWg_5w28j1IcpwbseMtq5chMx0cmuVw/view?usp=drive_link"
                    title="Exposition S² - Vidéo 2D / Flyer"
                  />
                </div>
              </div>
              
              {/* PINOC & ENEMIES */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Pinoc & Enemies.mp4"
                    index={19}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/14A16ufiNPj_F2t7DmX2sgDM8l3429AKY/view?usp=drive_link"
                    title="Pinoc & Enemies - Vidéo 2D / Flyer"
                  />
                </div>
              </div>
              

              
              {/* YOUTH INTRO */}
              <div className="video-item flex flex-col">
                <div className="relative rounded shadow-md overflow-hidden">
                  <VideoWithSound
                    src="/videos/Youth Intro.mp4"
                    index={21}
                    videoRefs={videoRefs}
                    handleVideoClick={handleVideoClick}
                    driveUrl="https://drive.google.com/file/d/19M6Ce9CEKEbVQXHvc0Ju4rbTOQoG8uOk/view?usp=drive_link"
                    title="Youth Intro - Vidéo 2D / Flyer"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Écran de chargement désactivé */}
    </div>
  );
};

export default OnePage;
